<template>
    <common-modal :width="modalClass">
        <common-tabs v-if="tabs.length" ref="tabs" :tabs="tabs" :activeTab="tabs[0].name">
            <template v-if="users.length" #addUser>
                <common-content-wrapper width="medium">
                    <common-content-spacer height="medium">
                        <div class="user-list-container">
                            <common-grid-items>
                                <user-card
                                    v-for="companyUser in users"
                                    :key="companyUser.user.id"
                                    :companyUser="companyUser"
                                    :companyId="offer?.location?.companyServiceProvider?.id"
                                    :showAction="false"
                                    :isSelectionAvailable="true"
                                    @user-selected="userSelected"
                                    @company-role-changed="companyRoleChanged($event, companyUser.user.id)"
                                    @company-user-deleted="companyUserDeleted(companyUser.user.id)"
                                ></user-card>
                            </common-grid-items>
                        </div>

                        <common-buttons>
                            <common-button
                                type="secondary"
                                :label="$t('common.microcopy.false')"
                                :isSubmittable="false"
                                @click="emitEvent({ name: 'close' })"
                            ></common-button>
                            <common-button
                                type="primary"
                                :isSubmittable="false"
                                :label="$t('common.microcopy.add')"
                                @click="sendUsers()"
                            ></common-button>
                        </common-buttons>
                    </common-content-spacer>
                </common-content-wrapper>
            </template>
            <template #searchUser>
                <common-content-spacer height="medium">
                    <form-wrapper @form-submit="checkEmail">
                        <form-row v-if="!checkedUser" class="user-list-row">
                            <form-cell>
                                <text-input
                                    :formData="data"
                                    :label="$t('forms.auth.login.fields.email.label')"
                                    :placeholder="$t('forms.auth.login.fields.email.placeholder')"
                                    :isRequired="true"
                                    type="email"
                                    valuePath="email"
                                    @change="change"
                                ></text-input>
                            </form-cell>
                            <common-button
                                class="user-list-row_button"
                                :isProcessing="isLoading"
                                type="secondary"
                                :label="$t('common.microcopy.check')"
                            ></common-button>
                        </form-row>
                        <div v-else class="user-list-result">
                            <common-content-spacer height="medium">
                                <div v-if="checkedUser.id">
                                    {{ $t('components.availability.add_availability.user_modal.found_user') }}
                                    <div class="invite-user-item">
                                        <common-user-avatar size="medium" :user="checkedUser"></common-user-avatar>
                                        <span>{{ checkedUser.firstName }} {{ checkedUser.lastName }}</span>
                                    </div>
                                    <p class="invite-user-item__email">
                                        <span>{{ data.email }}</span>
                                    </p>
                                    <p>{{ $t('components.availability.add_availability.user_modal.invite_user') }}</p>
                                </div>
                                <div v-else>
                                    {{ $t('components.availability.add_availability.user_modal.user_email_not_found') }}
                                    <span class="invite-user-item__email">{{ data.email }}</span>
                                    {{ $t('components.availability.add_availability.user_modal.user_email_not_found_1') }}
                                </div>
                                <common-buttons class="user-list-result_button">
                                    <common-button
                                        type="secondary"
                                        :label="$t('common.microcopy.false')"
                                        :isSubmittable="false"
                                        @click="clearCheckedUser()"
                                    ></common-button>
                                    <common-button
                                        type="primary"
                                        :isSubmittable="false"
                                        :label="$t('common.microcopy.true')"
                                        @click="addCheckedUser()"
                                    ></common-button>
                                </common-buttons>
                            </common-content-spacer>
                        </div>
                    </form-wrapper>
                    <common-content-spacer height="medium" class="">
                        <div v-if="invitedUsers.length" class="invite-user-container">
                            <p>{{ $t('components.availability.add_availability.user_modal.user_list') }}</p>
                            <div v-for="(user, index) in invitedUsers" :key="index" class="invite-user-item">
                                <common-user-avatar size="medium" :user="user.user"></common-user-avatar>
                                <span v-if="user.id">{{ user.firstName }} {{ user.lastName }}</span>
                                <span v-else invite-user-item__email>{{ user.email }}</span>
                            </div>
                        </div>
                        <common-button
                            class="invite-user-container_button"
                            :isDisabled="!invitedUsers.length"
                            type="primary"
                            :isSubmittable="false"
                            :label="$t('common.microcopy.add')"
                            @click="addInvitedUsers()"
                        ></common-button>
                    </common-content-spacer>
                </common-content-spacer>
            </template>
        </common-tabs>
    </common-modal>
</template>

<script>
    import UserCard from 'components/page-elements/user/UserCard'
    import TextInput from 'components/form-inputs/TextInput'
    import UsersAPI from 'api/UsersAPI'
    import FormMixin from 'mixins/FormMixin'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [FormMixin, WindowResizeMixin],

        components: {
            UserCard,
            TextInput,
        },
        props: {
            offer: { type: Object, required: true },
            users: { type: Array, required: true },
            companyUsers: { type: Array, required: true },
        },

        data() {
            return {
                selectedUsers: [],
                isLoading: false,
                invitedUsers: [],
                checkedUser: null,
                tabs: [],
            }
        },

        computed: {
            isCompanyOfferOwner() {
                return this.offer.location?.companyServiceProvider
            },

            isUserOfferOwner() {
                return this.offer.location?.userServiceProvider
            },

            modalClass() {
                return this.isTabletSmall ? 'large' : 'large'
            },
        },

        methods: {
            userSelected(payload) {
                if (payload.isSelected) {
                    this.selectedUsers.push(Number(payload.id))
                } else {
                    const index = this.selectedUsers.indexOf(payload.id)
                    this.selectedUsers.splice(index, 1)
                }
            },

            sendUsers() {
                const payload = this.users.filter((user) => this.selectedUsers.includes(Number(user?.user?.id)))
                this.emitEvent({ name: 'add-users', payload })
            },

            addCheckedUser() {
                this.invitedUsers.push(this.checkedUser)
                this.clearCheckedUser()
            },

            clearCheckedUser() {
                this.checkedUser = null
                this.data.email = ''
            },

            addInvitedUsers() {
                this.emitEvent({ name: 'add-invited-users', payload: this.invitedUsers })
            },

            checkEmail() {
                if (
                    this.$auth.getUser().email === this.data.email ||
                    this.invitedUsers.map((us) => us.email).includes(this.data.email)
                ) {
                    this.$toast.error(this.$t('components.availability.add_availability.errors.user_already_added'))
                    this.clearCheckedUser()
                    return
                }
                UsersAPI.getUserbyEmail(this.data.email)
                    .then((response) => {
                        if (this.companyUsers?.map((user) => user.user.id).includes(response.id)) {
                            this.$toast.error(this.$t('components.availability.add_availability.errors.user_in_company'))
                            this.clearCheckedUser()
                            return
                        }
                        this.checkedUser = response
                    })
                    .catch((err) => {
                        if (err.error.message === 'USER.NOT_FOUND') {
                            const user = {
                                email: this.data.email,
                            }
                            this.checkedUser = user
                        }
                    })
            },

            setTabs() {
                this.tabs = [
                    {
                        name: 'searchUser',

                        displayName: this.$t('components.availability.add_availability.user_availability.new_users'),
                        icon: 'calendar',
                        withPadding: true,
                    },
                ]
                if (this.offer?.location?.companyServiceProvider?.id && this.users.length) {
                    this.tabs.unshift({
                        name: 'addUser',
                        displayName: this.$t('components.availability.add_availability.user_availability.users'),

                        icon: 'profile',
                        withPadding: true,
                    })
                }
            },
        },

        mounted() {
            this.setTabs()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';

    .checkbox-input__option label {
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .user-card--selected__icon {
        display: none;
    }
    .user-list-container {
        max-height: 500px;
        min-height: 500px;
        overflow-y: auto;
    }

    .invite-user-container {
        margin-top: 20px;
        max-height: 300px;
        overflow-y: auto;
        @include breakpoint(smartphone-large) {
            width: 280px;
            overflow: hidden;
        }

        &_button {
            @include breakpoint(tablet-small) {
                margin-left: 35px;
            }
        }
    }

    .user-list-row {
        align-items: flex-end !important;
        &_button {
            @include breakpoint(tablet) {
                margin-right: 30px;
            }
            @include breakpoint(tablet-small) {
                margin-left: 50px;
                margin-top: 10px;
            }
        }
    }

    .user-list-result {
        align-items: flex-end !important;
        width: 85%;
        overflow: hidden;
        &_button {
            @include breakpoint(tablet-small) {
                max-width: 80%;

                margin-left: 20px;
                margin-top: 10px;
            }
        }
    }

    .invite-user-item {
        display: flex;
        align-items: center;
        margin-bottom: 10px;

        &__email {
            color: blue;
        }

        .avatar {
            margin-right: 10px;
            img {
                max-width: 100% !important;
                margin: 0;
            }
        }
    }
</style>
